/**
 * `exerciseId` → name, for a handful of ids — the lean batch, with its one trap folded in.
 *
 * ── The trap, in one place instead of five ───────────────────────────────────────────────────
 * `exercisesByIdsQueryOptions` is DISABLED for an empty list, and **a disabled query that has never
 * fetched reports `isPending` for ever**: TanStack v5 derives the flag from "no data and no error",
 * not from "a request is in flight", and a query that will never run satisfies that for the life of
 * the screen. A caller that waits on the flag alone therefore waits for ever — the Ankiety tab sat
 * in its skeleton permanently for every form without a clip question, which put the empty state
 * below it out of reach too.
 *
 * So `pending` here carries the ENABLEMENT with it, through the same predicate the options use. The
 * explanation used to be written out at five call sites and in two documents; it lives here now,
 * and a caller reads `pending` without having to know any of it.
 */

import { useQuery } from '@tanstack/react-query';

import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import { exercisesByIdsQueryOptions } from '../api/exerciseQueries';
import { toExerciseListItems } from '../model/exerciseListItem';

export interface ExerciseNamesState {
  /** Empty until the batch answers — and for ever when there was nothing to ask about. */
  readonly names: ReadonlyMap<number, string>;
  /** A read was MADE and has not answered. False when no id was asked about. */
  readonly pending: boolean;
  readonly failed: boolean;
}

export function useExerciseNames(ids: readonly number[]): ExerciseNamesState {
  // The content language is read here rather than taken as a parameter: every caller answered it
  // the same way, and threading it made three components carry a value they never looked at.
  const exercises = useQuery(exercisesByIdsQueryOptions(ids, useContentLanguage()));
  return {
    names: new Map(
      (exercises.isSuccess ? toExerciseListItems(exercises.data) : []).map((item) => [
        item.id,
        item.name,
      ]),
    ),
    pending: ids.length > 0 && exercises.isPending,
    failed: exercises.isError,
  };
}
